import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'redux-first-history';
import { ConnectIconStart } from '@dev/ee-components';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from '@material-ui/core';
import { links } from '@/services/dictionaries/links';
import { APIPaths, SettingsPaths } from '@/services/dictionaries/Paths';
import { OnboardingVideoThumbnail } from '@/components/Onboarding/components/OnboardingVideoThumbnail';
import { SaveOnboardingStart, getOnboardingStore } from '@/store/onboarding/actions';
import { OnboardingTask, OnboardingSteps, OnboardingTaskProps } from '../../../types';
import SvgSmtpConnect from './svgs/svg-smtp-connect.svg';
import SvgSmtpReport from './svgs/svg-smtp-api-report.svg';

const taskId = 'sendEmailsViaSmtpTask';
const itemVideoId1 = 'WM1z8ZmKYP0';

export enum SendEmailsViaSmtpTaskSavedDataKeysEnum {
    SendEmailsViaSmtpTaskConnectWithYourTool = 'SendEmailsViaSmtpTaskConnectWithYourTool',
    SendEmailsViaSmtpTaskViewEmailLogs = 'SendEmailsViaSmtpTaskViewEmailLogs',
    SendEmailsViaSmtpTaskSkipStep1 = 'SendEmailsViaSmtpTaskSkipStep1',
    SendEmailsViaSmtpTaskSkipStep2 = 'SendEmailsViaSmtpTaskSkipStep2',
    SendEmailsViaSmtpTaskSkipStep3 = 'SendEmailsViaSmtpTaskSkipStep3',
}

export const useSendEmailsViaSmtpTask = ({ onboardingGoals }: OnboardingTaskProps): OnboardingTask => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { savedData, data } = useSelector(getOnboardingStore);

    const goToDomains = (): void => {
        dispatch(push(SettingsPaths.Domains));
    };

    const goToCreateSmtp = (): void => {
        dispatch(push(SettingsPaths.CreateSMTP));
    };

    const goToEmailLogs = (): void => {
        if (!savedData?.SendEmailsViaSmtpTaskViewEmailLogs) {
            dispatch(SaveOnboardingStart({ SendEmailsViaSmtpTaskViewEmailLogs: true }));
        }
        dispatch(push(APIPaths.LogsEmail));
    };

    const handleConnectWithToolDone = () => {
        dispatch(SaveOnboardingStart({ SendEmailsViaSmtpTaskConnectWithYourTool: true }));
    };

    const useSendEmailsViaSmtpSteps: OnboardingSteps = {
        id: taskId,
        title: t('sendemailsviasmtp'),
        items: [
            {
                title: t('createsmtpcredentials'),
                features: [
                    t('sendemailsviasmtp_createsmtpcredentials_feature1'),
                    <Trans
                        key="sendemailsviasmtp_createsmtpcredentials_feature2"
                        i18nKey="sendemailsviasmtp_createsmtpcredentials_feature2"
                        components={{ url: <Link onClick={goToDomains} /> }}
                    />,
                ],
                secondaryContent: <OnboardingVideoThumbnail videoId={itemVideoId1} />,
                primaryActionTitle: t('createsmtpcredentials'),
                primaryActionCallback: goToCreateSmtp,
                isSkipped: !!savedData?.SendEmailsViaSmtpTaskSkipStep1,
                skipSavedDataKey: SendEmailsViaSmtpTaskSavedDataKeysEnum.SendEmailsViaSmtpTaskSkipStep1,
                isCompleted: data?.OneSmtp,
            },
            {
                title: t('connectwithyourtool'),
                features: [
                    t('sendemailsviasmtp_connectwithyourtool_feature1'),
                    <Trans
                        key="sendemailsviasmtp_connectwithyourtool_feature2"
                        i18nKey="sendemailsviasmtp_connectwithyourtool_feature2"
                        components={{ url: <Link href={links.integrations} target="_blank" /> }}
                    />,
                ],
                secondaryContent: <SvgSmtpConnect />,
                primaryActionTitle: t('markasdone'),
                primaryActionCallback: handleConnectWithToolDone,
                isNotReady: !!savedData?.SendEmailsViaSmtpTaskSkipStep1 || !data?.OneSmtp,
                isSkipped: !!savedData?.SendEmailsViaSmtpTaskSkipStep2,
                skipSavedDataKey: SendEmailsViaSmtpTaskSavedDataKeysEnum.SendEmailsViaSmtpTaskSkipStep2,
                isCompleted: !!savedData?.SendEmailsViaSmtpTaskConnectWithYourTool,
            },
            {
                title: t('checklogs'),
                features: [
                    t('sendemailsviasmtp_checklogs_feature1'),
                    <Trans
                        key="sendemailsviasmtp_checklogs_feature2"
                        i18nKey="sendemailsviasmtp_checklogs_feature2"
                        components={{ url: <Link href={links.whatisemaildeliverystatus} target="_blank" /> }}
                    />,
                ],
                secondaryContent: <SvgSmtpReport />,
                primaryActionTitle: t('viewemaillogs'),
                primaryActionCallback: goToEmailLogs,
                isNotReady:
                    !!savedData?.SendEmailsViaSmtpTaskSkipStep1 ||
                    !!savedData?.SendEmailsViaSmtpTaskSkipStep2 ||
                    !data?.OneSmtp,
                isSkipped: !!savedData?.SendEmailsViaSmtpTaskSkipStep3,
                skipSavedDataKey: SendEmailsViaSmtpTaskSavedDataKeysEnum.SendEmailsViaSmtpTaskSkipStep3,
                isCompleted: !!savedData?.SendEmailsViaSmtpTaskViewEmailLogs,
            },
        ],
    };

    return {
        header: t('sendemailsviasmtp'),
        icon: <ConnectIconStart />,
        title: t('delivertransactionalemailswithease'),
        description: (
            <Trans
                i18nKey="sendemailsviasmtp_desc"
                components={{ url: <Link href={links.apiVsSmtp} target="_blank" /> }}
            />
        ),
        steps: [useSendEmailsViaSmtpSteps],
        activeStepsIndex: 0,
        isVisible: onboardingGoals.isConnectingViaSMTPAnswered,
    };
};
